import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface WafRow {
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly category: string;
  readonly mode: string;
  readonly severity: string;
  readonly enabled: boolean;
}

const CATEGORIES = [
  '', 'SQLI', 'XSS', 'RCE', 'LFI', 'RFI', 'SSRF', 'PATH_TRAVERSAL',
  'PROTOCOL', 'SCANNER', 'RATE_LIMIT', 'BOT', 'GENERIC',
];
const MODES = ['', 'OFF', 'DETECT', 'BLOCK'];
const ENABLED = ['', 'true', 'false'];

/**
 * GR L30.12 — Web Application Firewall (`/security/waf`). Rule catalogue of the
 * GR net-new `waf_rule` store: each rule's attack category, enforcement mode
 * (off / detect / block), severity and enabled flag. Filterable by category,
 * mode and enabled state.
 */
export function GrWafPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<WafRow[]>([]);
  const [category, setCategory] = useState('');
  const [mode, setMode] = useState('');
  const [enabled, setEnabled] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (mode) params.set('mode', mode);
    if (enabled) params.set('enabled', enabled);
    const qs = params.toString();
    apiRequest<{ data: WafRow[] }>(`/v1/security/waf/rules${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category, mode, enabled]);

  return (
    <main aria-label="security-waf">
      <h1>{t('gr30.waf.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.waf.labelCategory')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.waf.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.waf.labelMode')}
          <select aria-label="filter-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            {MODES.map((m) => <option key={m || 'all'} value={m}>{m || t('gr30.waf.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.waf.labelEnabled')}
          <select aria-label="filter-enabled" value={enabled} onChange={(e) => setEnabled(e.target.value)}>
            {ENABLED.map((e) => <option key={e || 'all'} value={e}>{e === '' ? t('gr30.waf.all') : e === 'true' ? t('gr30.waf.enabledOpt') : t('gr30.waf.disabledOpt')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.waf.loading')}</p> : (
        <>
          <p aria-label="waf-count">{t('gr30.waf.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.waf.empty')}</p> : (
            <table aria-label="waf-table">
              <thead><tr><th>{t('gr30.waf.colCode')}</th><th>{t('gr30.waf.colName')}</th><th>{t('gr30.waf.colCategory')}</th><th>{t('gr30.waf.colMode')}</th><th>{t('gr30.waf.colSeverity')}</th><th>{t('gr30.waf.colEnabled')}</th></tr></thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.ruleId}>
                    <td>{w.ruleCode}</td><td>{w.ruleName}</td><td>{w.category}</td>
                    <td>{w.mode}</td><td>{w.severity}</td><td>{w.enabled ? t('gr30.waf.yes') : t('gr30.waf.no')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
